// src/pages/UrdTemplateSettings.jsx
import React, {
  useEffect,
  useMemo,
  useState,
  useRef,
  useCallback,
} from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiCheck,
  FiEye,
  FiSave,
  FiRefreshCcw,
  FiSearch,
  FiGrid,
  FiList,
} from "react-icons/fi";
import toast from "react-hot-toast";

import {
  useGetUrdTemplatesQuery,
  useGetTenantUrdTemplateQuery,
  useSaveTenantUrdTemplateMutation,
} from "../toolkitServices/invoiceTemplateApi";

const Spinner = ({ size = 16, className = "" }) => (
  <span
    className={`inline-block animate-spin rounded-full border-2 border-current border-t-transparent ${className}`}
    style={{ width: size, height: size }}
  />
);

const fadeUp = (delay = 0) => ({
  hidden: { opacity: 0, y: 10 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { delay, duration: 0.4, ease: [0.16, 1, 0.3, 1] },
  },
});

const API_BASE = process.env.REACT_APP_API_BASE_URL || "";

const previewUrl = (p) => {
  if (!p) return "";
  if (p.startsWith("http")) return p;
  return `${API_BASE}${p}`;
};

const SkeletonCard = () => (
  <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden shadow-sm">
    <div className="h-36 sm:h-44 bg-slate-100 animate-pulse" />
    <div className="p-3">
      <div className="h-3 w-2/3 bg-slate-100 rounded animate-pulse" />
      <div className="mt-2 h-3 w-1/3 bg-slate-100 rounded animate-pulse" />
      <div className="mt-3 flex gap-2">
        <div className="h-9 w-24 bg-slate-100 rounded-full animate-pulse" />
        <div className="h-9 w-28 bg-slate-100 rounded-full animate-pulse" />
      </div>
    </div>
  </div>
);

const clamp = (v, min, max) => Math.min(max, Math.max(min, v));

function ZoomPanImage({ src, alt, onError }) {
  const wrapRef = useRef(null);

  const [imgSize, setImgSize] = useState({ w: 0, h: 0 });
  const [scale, setScale] = useState(1);
  const [baseScale, setBaseScale] = useState(1);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [drag, setDrag] = useState({ on: false, x: 0, y: 0 });
  const [broken, setBroken] = useState(false);
  const [ready, setReady] = useState(false);
  const [loading, setLoading] = useState(true);

  const pointers = useRef(new Map());
  const pinch = useRef({
    active: false,
    startDist: 0,
    startScale: 1,
    startMid: { x: 0, y: 0 },
  });

  const getWrapSize = () => {
    const wrap = wrapRef.current;
    if (!wrap) return { cw: 0, ch: 0 };
    return { cw: wrap.clientWidth || 0, ch: wrap.clientHeight || 0 };
  };

  const clampPos = useCallback(
    (nextPos, nextScale = scale) => {
      const { cw, ch } = getWrapSize();
      if (!cw || !ch || !imgSize.w || !imgSize.h) return nextPos;

      const sw = imgSize.w * nextScale;
      const sh = imgSize.h * nextScale;

      const maxX = Math.max(0, (sw - cw) / 2);
      const maxY = Math.max(0, (sh - ch) / 2);

      return {
        x: clamp(nextPos.x, -maxX, maxX),
        y: clamp(nextPos.y, -maxY, maxY),
      };
    },
    [imgSize.w, imgSize.h, scale],
  );

  const computeFit = useCallback(() => {
    const { cw, ch } = getWrapSize();
    if (!cw || !ch || !imgSize.w || !imgSize.h) return;

    const fit = Math.min(cw / imgSize.w, ch / imgSize.h);
    const fitScale = Number.isFinite(fit) && fit > 0 ? fit : 1;

    setBaseScale(fitScale);
    setScale(fitScale);
    setPos({ x: 0, y: 0 });
    setReady(true);
  }, [imgSize.w, imgSize.h]);

  useEffect(() => {
    setBroken(false);
    setReady(false);
    setLoading(true);
    setImgSize({ w: 0, h: 0 });
    setScale(1);
    setBaseScale(1);
    setPos({ x: 0, y: 0 });
    setDrag({ on: false, x: 0, y: 0 });
    pointers.current.clear();
  }, [src]);

  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;

    let ro;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(() => {
        requestAnimationFrame(() => computeFit());
      });
      ro.observe(wrap);
    }

    const onWinResize = () => requestAnimationFrame(() => computeFit());
    window.addEventListener("resize", onWinResize);

    return () => {
      window.removeEventListener("resize", onWinResize);
      ro?.disconnect?.();
    };
  }, [computeFit]);

  const zoomTo = useCallback(
    (nextScale) => {
      const min = baseScale * 0.75;
      const max = baseScale * 6;
      const s = clamp(nextScale, min, max);
      setScale(s);
      setPos((p) => clampPos(p, s));
    },
    [baseScale, clampPos],
  );

  const zoomBy = (delta) => zoomTo(scale + delta);

  const reset = () => {
    setReady(false);
    requestAnimationFrame(() => {
      setScale(baseScale);
      setPos({ x: 0, y: 0 });
      setReady(true);
    });
  };

  const onWheel = (e) => {
    e.preventDefault();
    if (!ready) return;

    const dir = e.deltaY > 0 ? -1 : 1;
    const step = 0.13 * baseScale;
    zoomBy(dir * step);
  };

  const startDrag = (clientX, clientY) => {
    setDrag({ on: true, x: clientX, y: clientY });
  };

  const moveDrag = (clientX, clientY) => {
    setDrag((d) => {
      if (!d.on) return d;
      const dx = clientX - d.x;
      const dy = clientY - d.y;
      setPos((p) => clampPos({ x: p.x + dx, y: p.y + dy }, scale));
      return { on: true, x: clientX, y: clientY };
    });
  };

  const endDrag = () => setDrag((d) => ({ ...d, on: false }));

  const dist = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
  const mid = (a, b) => ({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });

  const onPointerDown = (e) => {
    e.currentTarget.setPointerCapture?.(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (pointers.current.size === 2) {
      const pts = Array.from(pointers.current.values());
      pinch.current.active = true;
      pinch.current.startDist = dist(pts[0], pts[1]);
      pinch.current.startScale = scale;
      pinch.current.startMid = mid(pts[0], pts[1]);
      endDrag();
      return;
    }

    if (pointers.current.size === 1) {
      startDrag(e.clientX, e.clientY);
    }
  };

  const onPointerMove = (e) => {
    if (!ready) return;

    if (pointers.current.has(e.pointerId)) {
      pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    }

    if (pointers.current.size === 2 && pinch.current.active) {
      const pts = Array.from(pointers.current.values());
      const d0 = pinch.current.startDist || 1;
      const d1 = dist(pts[0], pts[1]);
      const ratio = d1 / d0;

      zoomTo(pinch.current.startScale * ratio);

      const m0 = pinch.current.startMid;
      const m1 = mid(pts[0], pts[1]);
      const dx = m1.x - m0.x;
      const dy = m1.y - m0.y;

      setPos((p) => clampPos({ x: p.x + dx, y: p.y + dy }, scale));
      pinch.current.startMid = m1;
      return;
    }

    if (pointers.current.size === 1 && drag.on) {
      moveDrag(e.clientX, e.clientY);
    }
  };

  const onPointerUp = (e) => {
    pointers.current.delete(e.pointerId);

    if (pointers.current.size < 2) {
      pinch.current.active = false;
    }

    if (pointers.current.size === 0) {
      endDrag();
    }
  };

  const lastTap = useRef(0);
  const onDoubleTap = () => {
    const now = Date.now();
    if (now - lastTap.current < 280) reset();
    lastTap.current = now;
  };

  return (
    <div className="relative h-full flex flex-col">
      <div className="absolute z-10 top-3 right-3 flex items-center gap-2">
        <button
          type="button"
          onClick={() => zoomBy(-0.15 * baseScale)}
          className="px-3 py-1.5 rounded-full bg-white/95 border border-slate-200 text-slate-800 text-[11px] font-semibold hover:bg-slate-50 shadow-sm"
        >
          −
        </button>

        <button
          type="button"
          onClick={() => zoomBy(0.15 * baseScale)}
          className="px-3 py-1.5 rounded-full bg-white/95 border border-slate-200 text-slate-800 text-[11px] font-semibold hover:bg-slate-50 shadow-sm"
        >
          +
        </button>

        <button
          type="button"
          onClick={reset}
          className="px-3 py-1.5 rounded-full bg-white/95 border border-slate-200 text-slate-800 text-[11px] font-semibold hover:bg-slate-50 shadow-sm"
        >
          Fit
        </button>
      </div>

      <div
        ref={wrapRef}
        className={`relative w-full flex-1 min-h-0 rounded-2xl overflow-hidden bg-white select-none ${
          drag.on ? "cursor-grabbing" : "cursor-grab"
        }`}
        style={{ touchAction: "none" }}
        onWheel={onWheel}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onClick={onDoubleTap}
      >
        {(loading || !ready) && !broken && (
          <div className="absolute inset-0 grid place-items-center bg-slate-50">
            <div className="flex items-center gap-2 text-slate-600 text-xs">
              <Spinner size={16} />
              Loading preview…
            </div>
          </div>
        )}

        {!broken ? (
          <img
            src={src}
            alt={alt}
            draggable={false}
            className="absolute left-1/2 top-1/2 will-change-transform"
            style={{
              opacity: ready ? 1 : 0,
              transform: `translate(calc(-50% + ${pos.x}px), calc(-50% + ${pos.y}px)) scale(${scale})`,
              transformOrigin: "center center",
              maxWidth: "none",
              maxHeight: "none",
              transition: ready ? "opacity 140ms ease-out" : "none",
            }}
            onLoad={(e) => {
              const w = e.currentTarget.naturalWidth || 0;
              const h = e.currentTarget.naturalHeight || 0;
              setImgSize({ w, h });

              requestAnimationFrame(() => {
                computeFit();
                requestAnimationFrame(() => computeFit());
              });

              setLoading(false);
            }}
            onError={(e) => {
              setBroken(true);
              setLoading(false);
              onError?.(e);
            }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-slate-500 text-sm">
            Preview not available
          </div>
        )}
      </div>

      <div className="mt-2 text-[10px] text-slate-500 text-center shrink-0">
        Drag to move • Pinch / Wheel / +/- to zoom • Double-tap to fit
      </div>
    </div>
  );
}

export default function UrdTemplateSettings() {
  const [selectedKey, setSelectedKey] = useState("");
  const [previewing, setPreviewing] = useState(null);
  const [query, setQuery] = useState("");
  const [view, setView] = useState("grid");

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setPreviewing(null);
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (!previewing) return;

    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = prev;
    };
  }, [previewing]);

  const {
    data: listRes,
    isLoading: listLoading,
    isFetching: listFetching,
    error: listError,
    refetch: refetchTemplates,
  } = useGetUrdTemplatesQuery(undefined, {
    refetchOnMountOrArgChange: false,
    refetchOnFocus: false,
    refetchOnReconnect: false,
  });

  const {
    data: selRes,
    isLoading: selLoading,
    isFetching: selFetching,
    error: selError,
    refetch: refetchSelected,
  } = useGetTenantUrdTemplateQuery(undefined, {
    refetchOnMountOrArgChange: false,
    refetchOnFocus: false,
    refetchOnReconnect: false,
  });

  const [saveTemplate, saveState] = useSaveTenantUrdTemplateMutation();

  const templates = useMemo(() => {
    const arr = listRes?.templates ?? listRes?.data ?? [];
    if (!Array.isArray(arr)) return [];

    return arr
      .map((t) => ({
        ...t,
        key: t.key || t.templateKey,
        name: t.name || t.templateName || t.templateKey || t.key,
        preview: t.preview || t.previewUrl || "",
      }))
      .filter((t) => !!t.key);
  }, [listRes]);

  const serverSelected =
    selRes?.urdTemplateKey ||
    selRes?.data?.urdTemplateKey ||
    selRes?.templateKey ||
    "";

  useEffect(() => {
    if (!selectedKey && serverSelected) {
      setSelectedKey(serverSelected);
    }
  }, [serverSelected, selectedKey]);

  const isBusy = listLoading || selLoading || saveState.isLoading;
  const isDirty = !!selectedKey && selectedKey !== serverSelected;

  const filtered = useMemo(() => {
    const q = String(query || "").trim().toLowerCase();
    if (!q) return templates;

    return templates.filter((t) => {
      const name = String(t?.name || "").toLowerCase();
      const key = String(t?.key || "").toLowerCase();
      return name.includes(q) || key.includes(q);
    });
  }, [templates, query]);

  const onSave = async () => {
    if (!selectedKey) return toast.error("Please select a template");

    try {
      await saveTemplate(selectedKey).unwrap();
      toast.success("URD template saved");
      refetchSelected();
    } catch (e) {
      toast.error(e?.data?.message || e?.error || "Failed to save");
    }
  };

  const title = "URD Template";
  const subtitle =
    "Choose how your URD receipts will look. This will apply for all new URD receipts.";

  return (
    <div className="relative bg-white shadow-[0_12px_40px_-18px_rgba(15,23,42,0.35)] rounded-2xl flex flex-col h-[calc(100vh-120px)] md:h-[calc(100vh-70px)] overflow-hidden border border-slate-200">
      <div className="relative px-3 sm:px-6 py-2.5 sm:py-3 text-white overflow-hidden">
        <div className="absolute inset-0 bg-[#9481ce]" />

        <div className="relative">
          <motion.div
            variants={fadeUp(0)}
            initial="hidden"
            animate="visible"
            className="flex items-start justify-between gap-3"
          >
            <div className="min-w-0">
              <div className="text-[15px] sm:text-lg font-extrabold tracking-tight">
                {title}
              </div>

              <div className="mt-0.5 text-[11px] sm:text-xs text-violet-100/95 leading-4">
                {subtitle}
              </div>
            </div>

            <div className="flex items-center gap-2">
              {(listFetching || selFetching) && (
                <span className="hidden sm:inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-white/15">
                  <Spinner size={10} /> refreshing…
                </span>
              )}

              <button
                type="button"
                onClick={() => {
                  refetchTemplates();
                  refetchSelected();
                }}
                className="inline-flex items-center gap-1 rounded-full bg-white/15 hover:bg-white/25 transition px-3 py-2 sm:py-1.5 text-[11px]"
                disabled={isBusy}
              >
                <FiRefreshCcw />
                <span className="hidden sm:inline">Refresh</span>
              </button>

              <button
                type="button"
                onClick={onSave}
                disabled={!isDirty || saveState.isLoading}
                className="hidden sm:inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white text-[#4c3fb4] text-xs font-extrabold hover:opacity-95 disabled:opacity-60 shadow-sm"
              >
                {saveState.isLoading ? <Spinner size={14} /> : <FiSave />}
                Save
              </button>
            </div>
          </motion.div>
        </div>
      </div>

      {(listError || selError) && (
        <div className="px-3 sm:px-6 py-2 text-xs border-b bg-rose-50 text-rose-700">
          {listError?.data?.message ||
            selError?.data?.message ||
            "Failed to load URD templates"}
        </div>
      )}

      <div className="px-3 sm:px-6 py-2.5 border-b bg-slate-50/70">
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between gap-2">
            <div className="text-[11px] sm:text-xs text-slate-700">
              <span className="text-slate-500">Current:</span>{" "}
              <span className="font-extrabold text-slate-900">
                {serverSelected || "—"}
              </span>
              {isDirty && (
                <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 text-[10px] font-semibold">
                  unsaved
                </span>
              )}
            </div>

            <div className="inline-flex rounded-full border border-slate-200 bg-white overflow-hidden">
              <button
                type="button"
                onClick={() => setView("grid")}
                className={`px-2.5 py-1.5 text-[11px] font-semibold inline-flex items-center gap-1 ${
                  view === "grid"
                    ? "bg-slate-900 text-white"
                    : "text-slate-700 hover:bg-slate-50"
                }`}
              >
                <FiGrid />
                <span className="hidden sm:inline">Grid</span>
              </button>

              <button
                type="button"
                onClick={() => setView("list")}
                className={`px-2.5 py-1.5 text-[11px] font-semibold inline-flex items-center gap-1 ${
                  view === "list"
                    ? "bg-slate-900 text-white"
                    : "text-slate-700 hover:bg-slate-50"
                }`}
              >
                <FiList />
                <span className="hidden sm:inline">List</span>
              </button>
            </div>
          </div>

          <div className="relative">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />

            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search URD templates…"
              className="w-full pl-9 pr-3 py-2 rounded-full border border-slate-200 bg-white text-sm outline-none focus:ring-2 focus:ring-violet-200 focus:border-violet-300"
            />
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-3 sm:px-6 py-3 sm:py-4 bg-white">
        {listLoading && (
          <div className="grid grid-cols-2 max-[420px]:grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
        )}

        {!listLoading && filtered.length === 0 && (
          <div className="py-14 text-center">
            <div className="mx-auto max-w-md">
              <div className="text-sm font-semibold text-slate-900">
                No URD templates found
              </div>

              <div className="mt-1 text-xs text-slate-500">
                Try a different search keyword.
              </div>

              <button
                type="button"
                onClick={() => setQuery("")}
                className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full border border-slate-200 text-slate-700 bg-white hover:bg-slate-50 text-sm font-semibold"
              >
                Clear search
              </button>
            </div>
          </div>
        )}

        {!listLoading && filtered.length > 0 && (
          <motion.div
            variants={fadeUp(0.05)}
            initial="hidden"
            animate="visible"
            className={
              view === "grid"
                ? "grid grid-cols-2 max-[420px]:grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4"
                : "space-y-3"
            }
          >
            {filtered.map((t) => {
              const isSelected = selectedKey === t.key;
              const isCurrent = serverSelected === t.key;

              return (
                <motion.button
                  key={t.key}
                  type="button"
                  whileHover={{ y: -3 }}
                  whileTap={{ scale: 0.99 }}
                  onClick={() => setSelectedKey(t.key)}
                  className={`group text-left rounded-2xl border bg-white overflow-hidden shadow-sm transition focus:outline-none ${
                    isSelected
                      ? "border-violet-400 ring-2 ring-violet-200"
                      : "border-slate-200 hover:border-violet-200"
                  }`}
                >
                  <div className="relative h-36 sm:h-44 bg-slate-50 overflow-hidden">
                    {t.preview ? (
                      <img
                        src={previewUrl(t.preview)}
                        alt={t.name}
                        className="w-full h-full object-contain bg-white transition duration-300 group-hover:scale-[1.02]"
                        loading="lazy"
                        onError={(e) => {
                          e.currentTarget.style.display = "none";
                        }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-slate-400 text-xs">
                        No preview
                      </div>
                    )}

                    <div className="absolute top-2 left-2 flex items-center gap-2">
                      {isCurrent && (
                        <span className="px-2 py-0.5 rounded-full bg-emerald-600 text-white text-[10px] font-semibold shadow-sm">
                          Current
                        </span>
                      )}

                      <AnimatePresence>
                        {isSelected && (
                          <motion.span
                            initial={{ opacity: 0, y: -6, scale: 0.96 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: -6, scale: 0.96 }}
                            className="px-2 py-0.5 rounded-full bg-[#6b59c6] text-white text-[10px] font-semibold shadow-sm inline-flex items-center gap-1"
                          >
                            <FiCheck className="text-[11px]" />
                            Selected
                          </motion.span>
                        )}
                      </AnimatePresence>
                    </div>

                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setPreviewing(t);
                      }}
                      className="absolute bottom-2 right-2 inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white/95 border border-slate-200 text-slate-800 text-[11px] font-semibold hover:bg-slate-50 shadow-sm"
                    >
                      <FiEye />
                      Preview
                    </button>
                  </div>

                  <div className="p-3">
                    <div className="text-[12px] sm:text-sm font-extrabold text-slate-900 truncate">
                      {t.name}
                    </div>

                    <div className="mt-0.5 text-[10px] sm:text-[11px] text-slate-500 truncate">
                      Key: <span className="font-mono">{t.key}</span>
                    </div>

                    <div className="mt-3 flex items-center gap-2">
                      <span
                        className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[11px] font-semibold border ${
                          isSelected
                            ? "bg-violet-50 text-[#6b59c6] border-violet-200"
                            : "bg-white text-slate-700 border-slate-200"
                        }`}
                      >
                        {isSelected ? "Selected" : "Tap to select"}
                      </span>

                      {isCurrent && (
                        <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[11px] font-semibold border bg-emerald-50 text-emerald-700 border-emerald-200">
                          Active
                        </span>
                      )}
                    </div>
                  </div>
                </motion.button>
              );
            })}
          </motion.div>
        )}
      </div>

      <div className="sm:hidden border-t px-3 py-2 bg-white">
        <button
          type="button"
          onClick={onSave}
          disabled={!isDirty || saveState.isLoading}
          className="w-full inline-flex justify-center items-center gap-2 px-4 py-2.5 rounded-full bg-[#6b59c6] text-white text-xs font-extrabold hover:opacity-95 disabled:opacity-60 shadow-sm"
        >
          {saveState.isLoading ? <Spinner size={14} /> : <FiSave />}
          Save Template
        </button>
      </div>

      <AnimatePresence>
        {previewing && (
          <motion.div
            className="fixed inset-0 z-[9999] bg-black/50 flex items-center justify-center p-3 sm:p-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setPreviewing(null)}
          >
            <motion.div
              className="w-full max-w-5xl h-[86vh] rounded-3xl shadow-2xl overflow-hidden bg-white flex flex-col"
              initial={{ scale: 0.92, y: 18, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              exit={{ scale: 0.92, y: 18, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-4 py-3 border-b flex items-center justify-between">
                <div>
                  <div className="text-sm font-bold text-slate-900">
                    {previewing.name}
                  </div>
                  <div className="text-[11px] text-slate-500">
                    Key: {previewing.key}
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => setPreviewing(null)}
                  className="px-3 py-1.5 rounded-full border border-slate-200 text-slate-700 text-xs hover:bg-slate-50"
                >
                  Close
                </button>
              </div>

              <div className="flex-1 min-h-0 p-3 bg-slate-100">
                {previewing.preview ? (
                  <ZoomPanImage
                    src={previewUrl(previewing.preview)}
                    alt={previewing.name}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-slate-500 text-sm bg-white rounded-2xl">
                    Preview not available
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}